import { forwardRef, type ReactNode } from 'react'
import { Meter as BaseMeter } from '@base-ui/react/meter'
import { Progress as BaseProgress } from '@base-ui/react/progress'
import { CircleAlert, CircleCheck, TriangleAlert, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import './progress.css'

type BarLayout = {
  /** Visible label. It also names the bar for assistive technology. */
  label: ReactNode
  /** Shows the formatted value beside the label. */
  showValue?: boolean
  /** Puts the label, bar and value on one line, for dense rows such as a table cell. */
  inline?: boolean
  className?: string
}

export type ProgressProps = Omit<BaseProgress.Root.Props, 'className' | 'children' | 'render'> & BarLayout

/** How much of a task is done, such as an upload. Pass `value={null}` while the total is unknown. */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(function Progress(
  { label, showValue = true, inline = false, className, ...root },
  ref,
) {
  return (
    <BaseProgress.Root ref={ref} className={cn('play-progress', inline && 'play-progress--inline', className)} {...root}>
      <div className="play-progress__header">
        <BaseProgress.Label className="play-progress__label">{label}</BaseProgress.Label>
        {showValue && <BaseProgress.Value className="play-progress__value" />}
      </div>
      <BaseProgress.Track className="play-progress__track">
        <BaseProgress.Indicator className="play-progress__indicator" />
      </BaseProgress.Track>
    </BaseProgress.Root>
  )
})

/** Where a meter's value sits: `low`/`high` past a single threshold, or `good`/`degraded`/`poor` with `levels`. */
export type MeterLevel = 'low' | 'high' | 'good' | 'degraded' | 'poor'

/**
 * Thresholds for a three-level reading, such as a stream quality score. A value at or past `poor` reads poor, at or
 * past `degraded` reads degraded, and anything else reads good. Their order sets the direction: `poor` below
 * `degraded` means lower is worse (quality, signal, battery); above it means higher is worse (memory, packet loss).
 */
export type MeterLevels = { degraded: number; poor: number }

type MeterThresholds =
  | {
      /** At or below this value the bar shows warning, for gauges where low is a problem, such as battery. */
      low?: number
      /** At or above this value the bar shows error, for gauges where high is a problem, such as memory. */
      high?: number
      levels?: never
    }
  | {
      /** Reads good, degraded or poor, in success, warning or error. Use instead of `low` and `high`. */
      levels: MeterLevels
      low?: never
      high?: never
    }

export type MeterProps = Omit<BaseMeter.Root.Props, 'className' | 'children' | 'render'> & BarLayout & MeterThresholds

const levelGlyph: Record<MeterLevel, LucideIcon> = {
  low: TriangleAlert,
  high: TriangleAlert,
  good: CircleCheck,
  degraded: TriangleAlert,
  poor: CircleAlert,
}

function meterLevel(value: number, { low, high, levels }: { low?: number; high?: number; levels?: MeterLevels }): MeterLevel | undefined {
  if (levels) {
    const past = (threshold: number) => (levels.poor <= levels.degraded ? value <= threshold : value >= threshold)
    return past(levels.poor) ? 'poor' : past(levels.degraded) ? 'degraded' : 'good'
  }
  return high !== undefined && value >= high ? 'high' : low !== undefined && value <= low ? 'low' : undefined
}

/** Where a measurement sits within a known range, such as memory or battery. Not for task completion. */
export const Meter = forwardRef<HTMLDivElement, MeterProps>(function Meter(
  { label, showValue = true, inline = false, low, high, levels, value, getAriaValueText, className, ...root },
  ref,
) {
  const level = meterLevel(value, { low, high, levels })
  const Glyph = level ? levelGlyph[level] : undefined
  return (
    <BaseMeter.Root
      ref={ref}
      value={value}
      data-level={level}
      getAriaValueText={getAriaValueText ?? (level ? (formatted) => `${formatted}, ${level}` : undefined)}
      className={cn('play-meter', inline && 'play-meter--inline', className)}
      {...root}
    >
      <div className="play-meter__header">
        <BaseMeter.Label className="play-meter__label">{label}</BaseMeter.Label>
        {showValue && (
          <BaseMeter.Value className="play-meter__value">
            {(formatted) => (
              <>
                {Glyph && <Glyph aria-hidden="true" />}
                {formatted}
              </>
            )}
          </BaseMeter.Value>
        )}
      </div>
      <BaseMeter.Track className="play-meter__track">
        <BaseMeter.Indicator className="play-meter__indicator" />
      </BaseMeter.Track>
    </BaseMeter.Root>
  )
})
